import { isEqual } from 'lodash'
import { type Component, type Ref, ref, watch } from 'vue'

import { errorParser } from '@/helpers'
import type { EditedField, GroupedWorkerConfigurationField } from '@/stores/workerConfiguration'
import type { UUID } from '@/types'
import type {
  FieldTypeOptions,
  WorkerConfigurationField,
  WorkerConfigurationFieldType,
} from '@/types/workerConfiguration'

import BoolField from './BoolField.vue'
import CorpusExportField from './CorpusExportField.vue'
import DictField from './DictField.vue'
import ElementTypeField from './ElementTypeField.vue'
import EnumField from './EnumField.vue'
import FieldGroup from './FieldGroup.vue'
import ModelField from './ModelField.vue'
import NumericField from './NumericField.vue'
import SecretField from './SecretField.vue'
import TextField from './TextField.vue'
import WorkerVersionField from './WorkerVersionField.vue'

/**
 * Defines constraints on what kinds of fields a component accepts.
 *
 * This helps make the typing easier to handle on field components.
 */
type FieldConstraint = Partial<Pick<WorkerConfigurationField, 'many' | 'editable' | 'type'>>

/**
 * Props that will be provided by the Form component to all fields.
 */
export interface FieldProps<Constraint extends FieldConstraint> {
  /**
   * Key of this field within the configuration.
   *
   * If the field is within a group, the key is prefixed with the parent key.
   * This cannot just be called `key` because `key` is a reserved Vue prop name.
   */
  fieldKey: string
  fieldDefinition: GroupedWorkerConfigurationField & Constraint
  /**
   * The worker on which we are creating a configuration.
   */
  workerId: UUID
  /**
   * The corpus with which the configuration should be validated.
   * When this is not set, any validation that requires a corpus should not execute.
   */
  corpusId: UUID | null
}

/**
 * Get a more strictly typed version of `EditedField`, provided as the v-model of each configuration field,
 * depending on a field constraint.
 */
export type FieldModel<Constraint extends FieldConstraint> = EditedField & {
  value: // Unless the constraint requires `many: false`, if the type supports `many: true`, we allow an array
  | (Constraint['many'] extends false
        ? never
        : (FieldTypeOptions & { name: Constraint['type']; many: true })['type'][])
    // Unless the constraint requires `many: true`, we allow a single value
    | (Constraint['many'] extends true
        ? never
        : (FieldTypeOptions & { name: Constraint['type'] })['type'])
    // Unless the constraint requires `editable: false`, the value can be null
    | (Constraint['editable'] extends false ? never : null)
}

export const FIELD_COMPONENTS: { [K in WorkerConfigurationFieldType]: Component } = {
  text: TextField,
  string: TextField,
  int: NumericField,
  float: NumericField,
  worker_version: WorkerVersionField,
  element_type: ElementTypeField,
  bool: BoolField,
  dict: DictField,
  enum: EnumField,
  group: FieldGroup,
  model: ModelField,
  secret: SecretField,
  corpus_export: CorpusExportField,
}

export class ValidationError extends Error {
  readonly messages: string[]

  constructor(messages: string[]) {
    super(messages.join(', '))
    this.messages = messages
  }
}

const EMPTY_VALUES: unknown[] = [null, undefined, '', {}, []]

/**
 * Common validation rules for all fields.
 * @throws {ValidationError}
 */
export function baseValidator(fieldDefinition: WorkerConfigurationField, value: unknown): void {
  if (fieldDefinition.required && EMPTY_VALUES.some((emptyValue) => isEqual(emptyValue, value)))
    throw new ValidationError(['This field is required'])
}

/**
 * Enable field validation, optionally with an additional field-specific validator.
 * @param customValidator A validation function that throws a ValidationError to
 * @returns A Vue `ref` that will contain any validation errors when they occur.
 */
export function defineValidator(
  fieldDefinition: WorkerConfigurationField,
  fieldData: EditedField,
  customValidator?: (value: unknown) => void | Promise<void>,
): { localErrors: Ref<string[]> } {
  const localErrors = ref<string[]>([])
  watch(
    () => fieldData.value,
    async (newValue) => {
      fieldData.loading = true
      const errors = []

      try {
        baseValidator(fieldDefinition, newValue)
      } catch (err) {
        if (err instanceof ValidationError) errors.push(...err.messages)
        else errors.push(errorParser(err))
      }

      if (customValidator) {
        try {
          await customValidator(newValue)
        } catch (err) {
          if (err instanceof ValidationError) errors.push(...err.messages)
          else errors.push(errorParser(err))
        }
      }

      localErrors.value = errors
      fieldData.valid = errors.length <= 0
      fieldData.loading = false
    },
    { immediate: true },
  )
  return { localErrors }
}
